// src/components/MobileLiveSession.js - VERSÃO CORRIGIDA COM TOKEN
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMobile } from '../hooks/useMobile';
import { useWalletMobile } from '../hooks/useWalletMobile';
import { useAuth } from '../hooks/useAuth'; // ✅ ADICIONADO
import { Camera, Clock, Video, StopCircle, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Componente de Live Session otimizado para dispositivos mobile
 * UI touch-friendly, gestos e performance mobile
 */
const MobileLiveSession = ({ onComplete, onError, onCancel }) => {
  const isMobile = useMobile();
  const { isConnected, currentAccount, isPolygon } = useWalletMobile();
  const { getToken } = useAuth(); // ✅ ADICIONADO para pegar o token

  // Refs
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  
  // Timers
  const liveTimerRef = useRef(null);
  const clipTimerRef = useRef(null);

  // Estado mobile-optimized
  const [sessionState, setSessionState] = useState({
    // Estados principais
    isLive: false,
    isRecording: false,
    isCounting: false,
    
    // Timers
    timeLeft: 60,
    countdown: 3,
    recordingTime: 5,
    
    // UI states
    cameraReady: false,
    hasPermission: false,
    error: null,
    clipRecorded: false,
    
    // Mobile-specific
    isFrontCamera: true,
    torchEnabled: false,
    orientation: 'portrait'
  });

  // Configurações otimizadas para mobile
  const MOBILE_CONFIG = {
    LIVE_DURATION: 60,
    CLIP_START_AT: 30,
    CLIP_DURATION: 5,
    VIDEO_CONSTRAINTS: {
      portrait: {
        width: { ideal: 720, max: 1080 },
        height: { ideal: 1280, max: 1920 },
        frameRate: { ideal: 24, max: 30 },
        aspectRatio: { ideal: 9/16 }
      },
      landscape: {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 24, max: 30 },
        aspectRatio: { ideal: 16/9 }
      }
    }
  };

  // Detectar orientação
  useEffect(() => {
    const handleOrientationChange = () => {
      const orientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
      setSessionState(prev => ({ ...prev, orientation }));
    };

    handleOrientationChange();
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Utilitários de vídeo mobile
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const normalizeBase64 = (base64) =>
    base64?.replace(/^data:[\w\/]+;[\w=,-]*;base64,/, 'data:video/webm;codecs=vp9,opus;base64,');

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // ✅ FUNÇÃO PARA UPLOAD DO CLIP COM TOKEN
  const uploadClipWithToken = async (clipBase64, duration, timestamp) => {
    try {
      console.log('📱 Iniciando upload mobile com token...');
      
      const token = getToken(); // ✅ USANDO getToken DO HOOK
      
      console.log('🔐 Token disponível para upload:', !!token);
      
      if (!token) {
        throw new Error('Token de autenticação não encontrado no mobile');
      }

      const uploadData = {
        clip: clipBase64,
        duration: duration,
        timestamp: timestamp,
        isMobile: true,
        walletAddress: currentAccount
      };

      console.log('📤 Enviando dados para API mobile...');

      const response = await fetch('https://plexo-natural-api.onrender.com/api/complete-live', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`, // ✅ TOKEN INCLUÍDO
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(uploadData)
      });

      console.log('📡 Status da resposta mobile:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro detalhado da API:', errorText);
        throw new Error(`Erro HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Resposta da API mobile:', result);

      return result;

    } catch (error) {
      console.error('❌ Erro no upload mobile:', error);
      throw error;
    }
  };

  // Inicializar câmera mobile
  const initializeMobileCamera = useCallback(async () => {
    try {
      console.log('📱 Inicializando câmera mobile...');

      const constraints = {
        video: {
          ...MOBILE_CONFIG.VIDEO_CONSTRAINTS[sessionState.orientation],
          facingMode: sessionState.isFrontCamera ? 'user' : 'environment',
          torch: sessionState.torchEnabled
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
        
        // Play com tratamento de erro mobile
        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn('⚠️ Autoplay bloqueado:', playError);
          // Em mobile, usuário precisa interagir para dar play
        }
      }

      streamRef.current = stream;
      
      setSessionState(prev => ({
        ...prev,
        cameraReady: true,
        hasPermission: true,
        error: null
      }));

      console.log('✅ Câmera mobile inicializada');

    } catch (error) {
      console.error('❌ Erro na câmera mobile:', error);
      
      let errorMessage = 'Erro ao acessar câmera';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Permissão de câmera negada. Ative nas configurações.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'Câmera não encontrada';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Navegador não suporta gravação de vídeo';
      }

      setSessionState(prev => ({ 
        ...prev, 
        error: errorMessage,
        hasPermission: false 
      }));

      if (onError) onError(errorMessage);
    }
  }, [sessionState.orientation, sessionState.isFrontCamera, sessionState.torchEnabled, onError]);

  // Iniciar live session
  const startLiveSession = useCallback(async () => {
    if (!isConnected) {
      const errorMsg = 'Conecte sua carteira antes de iniciar a live';
      setSessionState(prev => ({ ...prev, error: errorMsg }));
      if (onError) onError(errorMsg);
      return;
    }

    if (!sessionState.hasPermission) {
      await initializeMobileCamera();
      return;
    }

    console.log('🎥 Iniciando live session mobile...');
    setSessionState(prev => ({ 
      ...prev, 
      isLive: true,
      timeLeft: MOBILE_CONFIG.LIVE_DURATION,
      error: null 
    }));
  }, [isConnected, sessionState.hasPermission, initializeMobileCamera, onError]);

  // Gravar clip (mobile-optimized)
  const startClipRecording = useCallback(() => {
    if (!streamRef.current) {
      console.error('❌ Stream não disponível para gravação');
      return;
    }

    console.log('🎬 Iniciando gravação de clip mobile...');

    // Countdown para gravação
    setSessionState(prev => ({ ...prev, isCounting: true }));
    
    let count = 3;
    const countdownInterval = setInterval(() => {
      setSessionState(prev => ({ ...prev, countdown: count }));
      count--;

      if (count < 0) {
        clearInterval(countdownInterval);
        
        setSessionState(prev => ({
          ...prev,
          isCounting: false,
          isRecording: true,
          recordingTime: MOBILE_CONFIG.CLIP_DURATION
        }));

        recordedChunksRef.current = [];

        // Configurar MediaRecorder para mobile
        const options = {
          mimeType: 'video/webm;codecs=vp9,opus',
          videoBitsPerSecond: 2500000 // Bitrate otimizado para mobile
        };

        try {
          const recorder = new MediaRecorder(streamRef.current, options);
          mediaRecorderRef.current = recorder;

          recorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              recordedChunksRef.current.push(e.data);
            }
          };

          recorder.onstop = async () => {
            console.log('⏹️ Gravação finalizada - chunks:', recordedChunksRef.current.length);
            
            if (recordedChunksRef.current.length > 0) {
              setSessionState(prev => ({ 
                ...prev, 
                clipRecorded: true,
                isRecording: false 
              }));
            } else {
              setSessionState(prev => ({ ...prev, isRecording: false }));
            }
          };

          // Gravar em chunks menores para mobile
          recorder.start(500); // 500ms chunks
          console.log('🎥 Gravando clip...');

        } catch (error) {
          console.error('❌ Erro no MediaRecorder:', error);
          setSessionState(prev => ({ 
            ...prev, 
            isRecording: false,
            error: 'Erro ao iniciar gravação' 
          }));
        }
      }
    }, 1000);
  }, []);

  // Timer da live session
  useEffect(() => {
    if (!sessionState.isLive || sessionState.timeLeft <= 0) return;

    liveTimerRef.current = setInterval(() => {
      setSessionState(prev => {
        const newTime = prev.timeLeft - 1;

        // Trigger do clip
        if (newTime === MOBILE_CONFIG.CLIP_START_AT && !prev.isRecording) {
          startClipRecording();
        }

        // Finalizar live
        if (newTime <= 0) {
          clearInterval(liveTimerRef.current);
          completeLiveSession();
          return { ...prev, timeLeft: 0 };
        }

        return { ...prev, timeLeft: newTime };
      });
    }, 1000);

    return () => clearInterval(liveTimerRef.current);
  }, [sessionState.isLive, sessionState.timeLeft, startClipRecording]);

  // Timer do clip
  useEffect(() => {
    if (!sessionState.isRecording || sessionState.recordingTime <= 0) return;

    clipTimerRef.current = setInterval(() => {
      setSessionState(prev => {
        const newTime = prev.recordingTime - 1;
        
        if (newTime <= 0) {
          mediaRecorderRef.current?.stop();
          clearInterval(clipTimerRef.current);
        }
        
        return { ...prev, recordingTime: newTime };
      });
    }, 1000);

    return () => clearInterval(clipTimerRef.current);
  }, [sessionState.isRecording, sessionState.recordingTime]);

  // ✅ FINALIZAR LIVE SESSION CORRIGIDA - COM UPLOAD E TOKEN
  const completeLiveSession = useCallback(async () => {
    console.log('🚀 Finalizando live session mobile...');

    try {
      // Parar gravações
      mediaRecorderRef.current?.stop();
      
      // Parar stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Processar clip
      let finalClipBase64 = null;
      if (recordedChunksRef.current.length > 0) {
        const blob = new Blob(recordedChunksRef.current, { 
          type: 'video/webm;codecs=vp9,opus' 
        });
        finalClipBase64 = await blobToBase64(blob);
        finalClipBase64 = normalizeBase64(finalClipBase64);
        
        console.log('✅ Clip processado - tamanho:', finalClipBase64?.length);
      }

      if (!finalClipBase64) {
        throw new Error('Clip não foi capturado corretamente');
      }

      // ✅ FAZER UPLOAD COM TOKEN ANTES DE COMPLETAR
      console.log('📱 Fazendo upload do clip com token...');
      const uploadResult = await uploadClipWithToken(
        finalClipBase64, 
        MOBILE_CONFIG.LIVE_DURATION, 
        new Date().toISOString()
      );

      console.log('✅ Upload mobile concluído:', uploadResult);

      // Preparar resultado com dados do upload
      const result = {
        success: true,
        clip: finalClipBase64,
        duration: MOBILE_CONFIG.LIVE_DURATION,
        timestamp: new Date().toISOString(),
        isMobile: true,
        walletConnected: isConnected,
        isPolygon: isPolygon,
        userAddress: currentAccount,
        // ✅ INCLUIR DADOS DO UPLOAD
        alreadyUploaded: true,
        processedByLiveSession: true,
        tokensEarned: uploadResult.tokensEarned || 10,
        totalTokens: uploadResult.totalTokens,
        feedback: uploadResult.feedback || 'Live mobile processada com sucesso!',
        frameUrl: uploadResult.frameUrl,
        uploadMethod: 'mobile-pinata'
      };

      // Notificar componente pai
      if (onComplete) {
        onComplete(result);
      }

      console.log('🎉 Live session mobile finalizada com sucesso');

    } catch (error) {
      console.error('❌ Erro ao finalizar live mobile:', error);
      
      const errorResult = {
        success: false,
        error: error.message,
        isMobile: true,
        errorType: 'upload_failed'
      };

      if (onError) onError(errorResult);
    }
  }, [isConnected, isPolygon, currentAccount, onComplete, onError, uploadClipWithToken]);

  // Cancelar live session
  const cancelLiveSession = useCallback(() => {
    console.log('⏹️ Cancelando live session...');

    // Limpar recursos
    mediaRecorderRef.current?.stop();
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    clearInterval(liveTimerRef.current);
    clearInterval(clipTimerRef.current);

    // Resetar estado
    setSessionState({
      isLive: false,
      isRecording: false,
      isCounting: false,
      timeLeft: MOBILE_CONFIG.LIVE_DURATION,
      countdown: 3,
      recordingTime: MOBILE_CONFIG.CLIP_DURATION,
      cameraReady: false,
      hasPermission: false,
      error: null,
      clipRecorded: false,
      isFrontCamera: true,
      torchEnabled: false,
      orientation: 'portrait'
    });

    recordedChunksRef.current = [];

    if (onCancel) onCancel();
  }, [onCancel]);

  // Alternar câmera (front/back)
  const toggleCamera = useCallback(async () => {
    setSessionState(prev => ({ 
      ...prev, 
      isFrontCamera: !prev.isFrontCamera 
    }));

    // Reinicializar câmera com nova configuração
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    await initializeMobileCamera();
  }, [initializeMobileCamera]);

  // Alternar flashlight
  const toggleTorch = useCallback(async () => {
    if (!streamRef.current) return;

    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (videoTrack && typeof videoTrack.applyConstraints === 'function') {
      try {
        await videoTrack.applyConstraints({
          advanced: [{ torch: !sessionState.torchEnabled }]
        });
        
        setSessionState(prev => ({ 
          ...prev, 
          torchEnabled: !prev.torchEnabled 
        }));
      } catch (error) {
        console.warn('⚠️ Flash não suportado:', error);
      }
    }
  }, [sessionState.torchEnabled]);

  // Renderizar controles mobile
  const renderMobileControls = () => {
    if (!sessionState.isLive) {
      return (
        <div className="mobile-controls-initial">
          <button
            onClick={startLiveSession}
            disabled={!isConnected || sessionState.isCounting}
            className="mobile-btn-large bg-gradient-to-r from-red-500 to-pink-500 text-white py-5 rounded-full font-bold text-lg w-32 h-32 flex items-center justify-center mx-auto shadow-2xl"
          >
            {sessionState.hasPermission ? '🎥 GO LIVE' : '📷 Ativar Câmera'}
          </button>

          {!sessionState.hasPermission && (
            <p className="text-gray-500 text-center mt-4 text-sm">
              Toque para permitir acesso à câmera
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="mobile-controls-live">
        {/* Timer */}
        <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full font-bold">
          <Clock className="w-4 h-4 inline mr-2" />
          {formatTime(sessionState.timeLeft)}
        </div>

        {/* Indicador de gravação */}
        {sessionState.isRecording && (
          <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
            🔴 {sessionState.recordingTime}s
          </div>
        )}

        {/* Countdown */}
        {sessionState.isCounting && (
          <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center">
            <div className="text-8xl font-bold text-white animate-pulse">
              {sessionState.countdown}
            </div>
          </div>
        )}

        {/* Controles de câmera */}
        <div className="absolute bottom-24 left-4 flex flex-col gap-3">
          <button
            onClick={toggleCamera}
            className="w-12 h-12 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center"
          >
            🔄
          </button>
          
          <button
            onClick={toggleTorch}
            className="w-12 h-12 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center"
          >
            {sessionState.torchEnabled ? '💡' : '🔦'}
          </button>
        </div>

        {/* Botão cancelar */}
        <button
          onClick={cancelLiveSession}
          className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white py-3 px-8 rounded-full font-bold mobile-btn"
        >
          ⏹️ Finalizar
        </button>
      </div>
    );
  };

  // Renderizar preview da câmera
  const renderCameraPreview = () => {
    if (!sessionState.hasPermission && sessionState.error) {
      return (
        <div className="camera-error bg-gray-800 rounded-lg flex items-center justify-center h-64">
          <div className="text-center text-white p-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-lg font-medium mb-2">Erro na Câmera</p>
            <p className="text-sm opacity-80">{sessionState.error}</p>
            <button
              onClick={initializeMobileCamera}
              className="mt-4 bg-blue-500 text-white py-2 px-4 rounded-lg mobile-btn"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="camera-preview relative bg-black rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full ${sessionState.isLive ? 'h-96' : 'h-64'} object-cover`}
        />
        
        {/* Overlay quando não está live */}
        {!sessionState.isLive && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="text-center text-white">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-70" />
              <p className="text-sm opacity-70">Preview da Câmera</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Fallback para desktop
  if (!isMobile) {
    const LiveSession = require('./LiveSession').default;
    return <LiveSession onComplete={onComplete} onError={onError} />;
  }

  return (
    <div className="mobile-live-session min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-4">
        <h1 className="text-xl font-bold text-center">
          {sessionState.isLive ? '🎥 LIVE' : 'Sessão Mobile'}
        </h1>
      </div>

      {/* Conteúdo Principal */}
      <div className="p-4">
        {renderCameraPreview()}
        
        {/* Status da Carteira */}
        {!isConnected && (
          <div className="bg-yellow-500 text-white p-3 rounded-lg mt-4 text-center">
            <AlertCircle className="w-5 h-5 inline mr-2" />
            Conecte sua carteira para ganhar RUA tokens
          </div>
        )}

        {/* Controles */}
        <div className="mt-6">
          {renderMobileControls()}
        </div>

        {/* Informações */}
        <div className="mt-6 text-center text-gray-400 text-sm">
          <p>⚡ Live de 1 minuto + Clip de 5s automático</p>
          <p>💰 Ganhe RUA tokens na Polygon</p>
          <p>🔐 Autenticação segura com token</p>
        </div>
      </div>
    </div>
  );
};

export default MobileLiveSession;
